import { Accordion, Button, Drawer, DrawerProps, Radio } from '@mantine/core';
import React from 'react';
import { useNavigate } from 'react-router';
import { generatePath, useParams } from 'react-router-dom';

import { rootRoutes } from '../../../app/routers/rootRoutes';
import { logout } from '../../../app/stores/actions';
import { useAppDispatch, useAppSelector } from '../../../app/stores/hooks';
import { sortFieldOptions, SortingTask, taskGroupOptions, TaskGroups } from '../../../entities/tasks';
import { selectSort, setValues } from '../../../entities/tasks';
import { getOptionLabel } from '../../../entities/tasks/model/lib/data-filtering';
import { TaskReload } from '../../../entities/tasks/ui/TaskReload';
import styles from './TaskMenu.module.css';

export const TaskMenu: React.FC<DrawerProps> = ({ opened, onClose }) => {
  const dispatch = useAppDispatch();
  const { groupId = TaskGroups.None } = useParams();
  const navigate = useNavigate();
  const sort = useAppSelector(selectSort);
  const sortValue = sort.join('');

  const groupLabel = React.useMemo(() => {
    return groupId ? getOptionLabel(taskGroupOptions, groupId) : '';
  }, [groupId, taskGroupOptions]);

  const sortLabel = React.useMemo(() => {
    return getOptionLabel(sortFieldOptions, sortValue);
  }, [sortValue, sortFieldOptions]);

  const handleSortChange = (value: SortingTask) => () => {
    dispatch(setValues({ sort: value }));
    onClose();
  };

  const handleGroupChange = (groupId: TaskGroups) => () => {
    if (groupId === TaskGroups.None) {
      navigate(rootRoutes.taskList);
    } else {
      navigate(generatePath(rootRoutes.groupedTaskList, { groupId: encodeURI(groupId) }));
    }

    onClose();
  };

  const handleLogoutClick = () => {
    dispatch(logout());
  };

  return (
    <Drawer.Root data-id="taskSettings" opened={opened} size="xs" onClose={onClose}>
      <Drawer.Overlay />
      <Drawer.Content className={styles.drawerContent}>
        <Drawer.Body className={styles.drawerBody}>
          <div className={styles.header} />
          <div className={styles.content}>
            <Accordion
              classNames={{
                item: styles.accordionItem,
                label: styles.accordionLabel,
              }}
              defaultValue="group"
            >
              <Accordion.Item data-id="group" value="group">
                <Accordion.Control>
                  Группировать все задачи
                  <div className={styles.subTitle}>{groupLabel}</div>
                </Accordion.Control>

                <Accordion.Panel>
                  {taskGroupOptions.map(({ value, label }) => (
                    <Radio
                      checked={groupId === value}
                      className={styles.item}
                      key={value}
                      label={label}
                      size="sm"
                      variant="outline"
                      onChange={handleGroupChange(value)}
                    />
                  ))}
                </Accordion.Panel>
              </Accordion.Item>

              <Accordion.Item data-id="sort" value="sort">
                <Accordion.Control>
                  Сортировать все задачи
                  <div className={styles.subTitle}>{sortLabel}</div>
                </Accordion.Control>
                <Accordion.Panel>
                  {sortFieldOptions.map(({ label, value, values }) => (
                    <Radio
                      checked={sortValue === value}
                      className={styles.item}
                      key={value}
                      label={label}
                      size="sm"
                      variant="outline"
                      onChange={handleSortChange(values)}
                    />
                  ))}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          </div>

          <div className={styles.footer}>
            <TaskReload onClose={onClose} />
            <div className={styles.action}>
              <span></span>
              <Button variant="subtle" onClick={handleLogoutClick}>
                Выйти
              </Button>
            </div>
          </div>
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
};
